/**
 * Tab key resolution shared by the two project detail surfaces.
 *
 * Each surface retired a different set of `?tab=` keys into the tabs that
 * absorbed them, so each keeps its own map and its own live key list. The
 * lookup itself carries no surface-specific behavior, which is why it lives
 * here and takes both as parameters.
 */

/**
 * Resolve a raw `?tab=` value to a live tab key on one surface.
 *
 * A retired key is aliased to the tab that absorbed it first; the result is
 * then membership-tested against the surface's live keys, so a key this
 * surface does not have resolves to null rather than to itself.
 *
 * @param raw The `tab` query parameter, or null when it is absent.
 * @param retiredKeys This surface's retired key to absorbing tab map.
 * @param liveKeys This surface's live tab keys.
 * @returns The live tab key, or null when `raw` is absent or unknown here.
 */
export function resolveTabKey<T extends string>(
	raw: string | null,
	retiredKeys: Readonly<Record<string, T>>,
	liveKeys: readonly T[],
): T | null {
	if (!raw) return null;
	const key = retiredKeys[raw] ?? raw;
	return liveKeys.includes(key as T) ? (key as T) : null;
}

/**
 * Decide whether a resolved tab key should replace the raw one in the URL.
 *
 * Rewriting a retired key leaves a copied address bar carrying a key the
 * surface still uses. Both surfaces read one `?tab=` parameter, though, so a
 * key this surface retired may be a live tab on the other one — and rewriting
 * that one turns a URL that works for the other reader into a URL that lands
 * them on Overview. The raw key is the only form both surfaces resolve, so it
 * is the form that stays.
 *
 * @param raw The `tab` query parameter, or null when it is absent.
 * @param resolved The live key `resolveTabKey` produced for this surface.
 * @param otherSurfaceLiveKeys The OTHER surface's live tab keys.
 * @returns True when the URL should be rewritten to `resolved`.
 */
export function shouldRewriteTabKey(
	raw: string | null,
	resolved: string,
	otherSurfaceLiveKeys: readonly string[],
): boolean {
	if (raw === null || raw === resolved) return false;
	return !otherSurfaceLiveKeys.includes(raw);
}
